import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Modal, Typography, message } from "antd";
import {
  createFactoryParcel,
  FactoryCreateParcelInput,
  checkSpeedShippingPrice,
} from "services/factoryParcelApi";
import type { CheckPriceInput } from "services/apiSpeedShipping";
import { CheckPriceCarrierItem } from "services/apiSpeedShipping";
import {
  getCheckPriceCarrierCode,
  normalizeSpeedLogo,
} from "../ParcelShared/speedCarrierUtils";
import { useGetMerchantList } from "hooks/useMerchant";
import { useAppSelector } from "store";
import ParcelFormFooter from "./form/ParcelFormFooter";
import ParcelFormSectionGate from "./form/ParcelFormSectionGate";
import ParcelDestinationStep from "./steps/ParcelDestinationStep";
import ParcelProductShippingStep from "./steps/ParcelProductShippingStep";
// import ParcelOthersSection from "./steps/ParcelOthersSection"; // ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว
import ParcelCreateConfirmModal, {
  type ParcelCreateConfirmSummary,
} from "./ParcelCreateConfirmModal";
import { MANAGE_PARCEL_LIST_PATH } from "../ParcelShared/parcelRoutes";
import InsufficientCreditModal from "../ParcelShared/InsufficientCreditModal";
import { useFactoryCreditBalance, useFactoryCodCreditInfo } from "hooks/useFactoryCredit";
import { useParcelCreateSteps } from "./hooks/useParcelCreateSteps";
import { useParcelPackagingSelection } from "./hooks/useParcelPackagingSelection";
import {
  ParcelFormControlsScope,
  ParcelFormOuter,
  ParcelFormSectionInRow,
  ParcelFormStack,
  ParcelFormZoneDivider,
  // PARCEL_FORM_ROW_GUTTER,
  // parcelFormColStyle,
} from "../manageParcelLayout";
import {
  formatPickupAddressDisplay,
  merchantToCheckPriceOrigin,
  pickDefaultSpeedMerchant,
} from "./parcelPickupOrigin";

const { Text } = Typography;

type ParcelCreateFormProps = {
  onCreated?: () => void;
};

const ParcelCreateForm: FC<ParcelCreateFormProps> = ({ onCreated }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [originForPrice, setOriginForPrice] = useState<CheckPriceInput["origin"] | null>(null);
  const factoryId = useAppSelector((state) => state.auth.profile?.user?.role?.ref_id) || "";
  const { data: merchantListRes } = useGetMerchantList(factoryId);
  const pickupMerchant = useMemo(
    () => pickDefaultSpeedMerchant(merchantListRes?.data),
    [merchantListRes]
  );
  const [carriers, setCarriers] = useState<CheckPriceCarrierItem[]>([]);
  const [selectedCarrier, setSelectedCarrier] = useState<string>("");
  const [checkingPrice, setCheckingPrice] = useState(false);
  const [speedHasSearched, setSpeedHasSearched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState<ParcelCreateConfirmSummary | null>(
    null
  );

  const steps = useParcelCreateSteps(form, !!pickupMerchant);
  const packagingSelection = useParcelPackagingSelection(form);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditModalPrice, setCreditModalPrice] = useState<number | null>(null);
  const { data: creditData } = useFactoryCreditBalance();
  // multiplier ปัจจุบัน — ใช้แค่แสดง "เครดิตที่ใช้" ให้ตรงกับโควต้าจริงที่โรงงานเห็น การเช็คเครดิต
  // พอ/ไม่พอ (handleOpenConfirm) ยังเทียบกับ available_credit ดิบเหมือนเดิม ไม่เกี่ยวกับ multiplier
  const { data: codCreditInfo } = useFactoryCodCreditInfo();
  const multiplier = codCreditInfo?.cod_credit_multiplier ?? 2;

  useEffect(() => {
    setOriginForPrice(pickupMerchant ? merchantToCheckPriceOrigin(pickupMerchant) : null);
  }, [pickupMerchant]);

  const clearCarrierResults = () => {
    setCarriers([]);
    setSelectedCarrier("");
    setSpeedHasSearched(false);
  };

  const handleSearchCarriers = async () => {
    try {
      const fieldsToValidate: (string | string[])[] = [
        "parcel_name",
        "parent_packaging_type_id",
        "packaging_type_id",
        "box_width",
        "box_height",
        "box_length",
        "box_weight",
        ["destination", "province_id"],
        ["destination", "district_id"],
        ["destination", "sub_district_id"],
        ["destination", "fullname"],
        ["destination", "telephone1"],
        ["destination", "address"],
        ["destination", "county"],
        ["destination", "city"],
        ["destination", "state"],
        ["destination", "postcode"],
      ];
      const values = await form.validateFields(fieldsToValidate);

      if (!originForPrice) {
        message.error("ยังไม่มีจุดรับสินค้า Speed — ตรวจสอบการตั้งค่า Merchant");
        return;
      }

      const dest = values.destination;
      setCheckingPrice(true);
      const payload: CheckPriceInput = {
        box_width: values.box_width ?? 1,
        box_height: values.box_height ?? 1,
        box_length: values.box_length ?? 1,
        box_weight: values.box_weight,
        cod_amount: values.cod_amount ?? 0,
        origin: originForPrice,
        destination: {
          county: dest.county,
          city: dest.city,
          state: dest.state,
          postcode: dest.postcode,
        },
      };
      const res = await checkSpeedShippingPrice(payload);
      const list = Array.isArray(res.data) ? res.data : [];
      setCarriers(list);
      setSpeedHasSearched(true);
      if (list.length > 0) {
        const code = getCheckPriceCarrierCode(list[0]);
        if (code) setSelectedCarrier(code);
      } else {
        message.warning("ไม่พบบริษัทขนส่งสำหรับเส้นทางนี้");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCheckingPrice(false);
    }
  };

  const buildConfirmSummary = (
    values: Awaited<ReturnType<typeof form.validateFields>>
  ): ParcelCreateConfirmSummary => {
    const selected = carriers.find(
      (c) => getCheckPriceCarrierCode(c) === selectedCarrier
    );
    const parent = packagingSelection.parentList.find(
      (p) => p.id === packagingSelection.parentPackagingTypeId
    );
    const packagingName = packagingSelection.selectedPackaging?.name;
    const typeLabel = [parent?.name, packagingName].filter(Boolean).join(" / ");

    const dest = values.destination ?? {};
    const pickupDisplay = pickupMerchant
      ? formatPickupAddressDisplay(pickupMerchant)
      : null;

    const parcelName = String(values.parcel_name ?? "").trim();

    return {
      parcelName,
      // products: [{ key: "single", name: parcelName, code: "ITEM1", qty: 1, pricePerUnit: 0 }],
      // เคยแสดงได้มากกว่า 1 รายการ (ตารางใน Modal สรุป)
      packaging: {
        typeLabel: typeLabel || undefined,
        width: values.box_width,
        length: values.box_length,
        height: values.box_height,
        weightGrams: values.box_weight,
      },
      sender: pickupMerchant
        ? {
            addressLine: pickupDisplay?.addressLine ?? "-",
            subDistrict: pickupDisplay?.county,
            district: pickupDisplay?.city,
            province: pickupDisplay?.state,
            postcode: pickupDisplay?.postcode,
            phone: pickupDisplay?.phone ?? "-",
          }
        : undefined,
      destination: {
        fullname: dest.fullname ?? "",
        addressLine: dest.address?.trim() || "-",
        subDistrict: dest.county,
        district: dest.city,
        province: dest.state,
        postcode: dest.postcode,
        telephone1: dest.telephone1 ?? "",
        telephone2: dest.telephone2?.trim() || undefined,
      },
      carrier: {
        code: selectedCarrier,
        name: selected?.name || selectedCarrier,
        logo: normalizeSpeedLogo(selected?.logo),
        shippingPrice: selected?.total
          ? Number(selected.total)
          : selected?.price
            ? Number(selected.price)
            : undefined,
        creditUsed: selected?.total
          ? Number(selected.total) * multiplier
          : selected?.price
            ? Number(selected.price) * multiplier
            : undefined,
      },
      // referenceNo: values.reference_no, // ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว
      // remark: values.remark,
      codAmount: values.cod_amount,
    };
  };

  const handleOpenConfirm = async () => {
    if (!selectedCarrier) {
      message.error("กรุณาค้นหาและเลือกขนส่งก่อน");
      return;
    }
    // J.5 ตรวจ credit vs ราคาขนส่งที่เลือก
    const selected = carriers.find((c) => getCheckPriceCarrierCode(c) === selectedCarrier);
    const estimatePrice = selected?.total ? Math.ceil(Number(selected.total)) : 0;
    const available = Number(creditData?.available_credit ?? 0);
    if (estimatePrice > 0 && available < estimatePrice) {
      setCreditModalPrice(estimatePrice);
      setCreditModalOpen(true);
      return;
    }
    try {
      const values = await form.validateFields();
      if (!values.parcel_name?.trim()) {
        message.warning("กรุณาระบุชื่อพัสดุ");
        return;
      }
      setConfirmSummary(buildConfirmSummary(values));
      setConfirmOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleConfirmCreate = async () => {
    setCreating(true);
    try {
      await performCreate();
    } finally {
      setCreating(false);
    }
  };

  const performCreate = async () => {
    if (!selectedCarrier) {
      message.error("กรุณาค้นหาและเลือกขนส่งก่อน");
      return;
    }
    try {
      const values = await form.validateFields();
      const selected = carriers.find(
        (c) => getCheckPriceCarrierCode(c) === selectedCarrier
      );
      const parcelName = String(values.parcel_name ?? "").trim();
      const boxWeight = values.box_weight;
      const parent = packagingSelection.parentList.find(
        (p) => p.id === packagingSelection.parentPackagingTypeId
      );
      const packagingName = packagingSelection.selectedPackaging?.name;
      const packagingTypeLabel = [parent?.name, packagingName].filter(Boolean).join(" / ");

      const body: FactoryCreateParcelInput = {
        origin_mode: "merchant",
        destination_address_id: values.destination?.address_id,
        carrier_code: selectedCarrier,
        box_width: values.box_width ?? 1,
        box_height: values.box_height ?? 1,
        box_length: values.box_length ?? 1,
        box_weight: boxWeight,
        cod_amount: values.cod_amount ?? 0,
        // reference_no: values.reference_no?.trim() || undefined, // ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว
        // remark: values.remark?.trim() || undefined,
        destination: {
          fullname: values.destination.fullname,
          address: values.destination.address,
          county: values.destination.county,
          city: values.destination.city,
          state: values.destination.state,
          postcode: values.destination.postcode,
          telephone1: values.destination.telephone1,
          telephone2: values.destination.telephone2?.trim() || undefined,
          email: values.destination.email,
          address_id: values.destination.address_id,
          province_id: values.destination.province_id,
          district_id: values.destination.district_id,
          sub_district_id: values.destination.sub_district_id,
        },
        products: [
          {
            name: parcelName,
            code: "ITEM1",
            qty: 1,
            price: 0,
            weight: Math.max(1, Math.round(Number(boxWeight) || 1)),
          },
        ],
        estimate_price: selected?.total
          ? Number(selected.total)
          : selected?.price
            ? Number(selected.price)
            : undefined,
        packaging_type_label: packagingTypeLabel || undefined,
      };
      const res = await createFactoryParcel(body);
      if (res.status === "success" && res.data?.parcel_id) {
        message.success(
          "บันทึกรายการแล้ว — กำลังเชื่อมต่อกับระบบขนส่งเพื่อขอหมายเลขพัสดุ (ดูสถานะที่รายการพัสดุ)"
        );
        setConfirmOpen(false);
        setConfirmSummary(null);
        form.resetFields();
        clearCarrierResults();
        onCreated?.();
        navigate(MANAGE_PARCEL_LIST_PATH);
      } else {
        message.error("สร้างพัสดุไม่สำเร็จ");
      }
    } catch (e: unknown) {
      const err = e as { message?: string; response?: { data?: { message?: string } } };
      const errMsg =
        err?.message || err?.response?.data?.message || "สร้างพัสดุไม่สำเร็จ";
      message.error(errMsg);
    }
  };

  const handleCloseConfirmModal = () => {
    if (creating) return;
    setConfirmOpen(false);
    setConfirmSummary(null);
  };

  const section2Enabled = steps.enabled.products;

  return (
    <>
      <ParcelFormOuter>
        <ParcelFormControlsScope>
          <ParcelFormStack>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                cod_amount: 0,
              }}
              onValuesChange={(changed) => {
                if (changed.destination || changed.parcel_name) {
                  clearCarrierResults();
                }
                if (
                  changed.parent_packaging_type_id ||
                  changed.packaging_type_id ||
                  changed.box_weight ||
                  changed.box_width ||
                  changed.box_height ||
                  changed.box_length
                ) {
                  clearCarrierResults();
                }
              }}
            >
              <ParcelFormSectionInRow>
                <ParcelDestinationStep
                  form={form}
                  onAddressChange={clearCarrierResults}
                  onCancel={() => navigate(MANAGE_PARCEL_LIST_PATH)}
                />

                <ParcelFormZoneDivider />

                <ParcelFormSectionGate
                  enabled={section2Enabled}
                  lockHint={steps.hints.products}
                >
                  <ParcelProductShippingStep
                    form={form}
                    carriers={carriers}
                    selectedCarrier={selectedCarrier}
                    onSelectCarrier={setSelectedCarrier}
                    speedHasSearched={speedHasSearched}
                    checkingPrice={checkingPrice}
                    onSearchCarriers={handleSearchCarriers}
                    onPackagingChange={clearCarrierResults}
                    sectionEnabled={section2Enabled}
                    availableCredit={Number(creditData?.available_credit ?? 0)}
                    multiplier={multiplier}
                  />
                </ParcelFormSectionGate>
              </ParcelFormSectionInRow>

                {/* ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว (เปิดใช้เมื่อต้องการ reference_no / remark)
                <Row gutter={PARCEL_FORM_ROW_GUTTER} align="stretch">
                  <Col xs={24} lg={12} style={parcelFormColStyle}>
                    <ParcelFormSectionGate
                      enabled={section2Enabled && !!selectedCarrier}
                      lockHint={steps.hints.others}
                    >
                      <ParcelOthersSection />
                    </ParcelFormSectionGate>
                  </Col>
                </Row>
                */}
            </Form>

            <ParcelFormFooter
              creating={creating}
              sectionEnabled={section2Enabled}
              canCreate={!!selectedCarrier && steps.addressComplete && steps.packagingComplete}
              onCreate={handleOpenConfirm}
            />
          </ParcelFormStack>
        </ParcelFormControlsScope>
      </ParcelFormOuter>

      <ParcelCreateConfirmModal
        open={confirmOpen}
        summary={confirmSummary}
        confirming={creating}
        mode="confirm"
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmCreate}
      />

      {/* J.5 Insufficient Credit Modal */}
      <InsufficientCreditModal
        open={creditModalOpen}
        onClose={() => setCreditModalOpen(false)}
        estimatePrice={creditModalPrice}
        multiplier={multiplier}
      />
    </>
  );
};

export default ParcelCreateForm;
